import { RadioField, RadioInput, TextareaField } from '@flos/react-ui';
import { useState } from 'react';
import { getElementFromElementId } from '../../../helpers/getElementFromElementId';
import { isRequired } from '../../../helpers/isRequired';
import { SummaryRowTemplate } from '../../../helpers/templates';
import useAppState from '../../../state/appState';
import { ClaimFormSubmitFieldValue } from '../../../types/claim-form-submit-field-value';
import { ElementValue, ElementValueHuman } from '../../../types/element-values';
import {
    OtherCompany as OtherInsuranceType,
    FormConfig,
    FormValues
} from '@topdanmark-onlinesolutions/claim-form-types';
import { getValue } from '../../values/Get';
import { getClaimFormSubmitValue } from '../../../helpers/getClaimFormSubmitValue';
import { radioOptions, CompanyType, contentElementId, isContentValid, getContentSubmitValue } from './util';
import InsuranceCompany from './InsuranceCompany';
import Bank from './Bank';

type fieldValueType = {
    companyType: CompanyType;
    remark: string;
};

const remarkId = 'evtBemrkninger';
const remarkLabel = 'Evt. bemærkninger';

const OtherCompany = ({ caption, elementId }: OtherInsuranceType) => {
    const formConfig = useAppState((state) => state.formConfig);
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const value = OtherCompany.getValueOrDefault(formValues, elementId);

    const [selectedOption, setSelectedOption] = useState<CompanyType>(value.companyType);
    const [remark, setRemark] = useState(value.remark);

    const isRequiredField = isRequired(formConfig, elementId);

    return (
        <>
            {caption && <h6>{caption}</h6>}
            <div className="u-spacing-stack-m">
                <RadioField
                    value={selectedOption}
                    required={isRequiredField}
                    onChange={({ target: { value } }) => {
                        setSelectedOption(value as CompanyType);
                        setFormValue(elementId, { companyType: value, remark });
                    }}
                    id={elementId}
                    key={elementId}
                >
                    {radioOptions.map((option, key) => (
                        <RadioInput
                            key={key}
                            label={option.caption}
                            value={option.value}
                            description={option.helpText}
                        />
                    ))}
                </RadioField>
            </div>
            {selectedOption === 'Forsikringsselskab' && <InsuranceCompany />}
            {selectedOption === 'Bank' && <Bank />}
            {selectedOption && (
                <div className="u-spacing-stack-m">
                    <TextareaField
                        value={remark}
                        onChange={({ target: { value } }) => {
                            setRemark(value);
                            setFormValue(elementId, { remark: value, companyType: selectedOption });
                        }}
                        id={remarkId}
                        key={remarkId}
                        label={remarkLabel}
                        maxLength={3000}
                    />
                </div>
            )}
        </>
    );
};

OtherCompany.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const element = getElementFromElementId(formConfig, elementId) as OtherInsuranceType;
    const { summaryCaption } = element;
    const value: fieldValueType = getValue(formConfig, formValues, elementId);
    const { companyType, remark } = value;

    if (value === undefined) {
        return <></>;
    }

    const contentSummaryRowTemplate: JSX.Element[] = [];
    const contentElements = contentElementId[companyType];

    Object.values(contentElements).forEach((id) => {
        const contentValue = formValues[id];
        const { label, value } = contentValue;
        contentSummaryRowTemplate.push(
            <SummaryRowTemplate key={id} elementId={elementId} caption={label} value={value} />
        );
    });

    return (
        <>
            <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={companyType} />
            {contentSummaryRowTemplate}
            {remark && <SummaryRowTemplate elementId={elementId} caption={remarkLabel} value={remark} />}
        </>
    );
};

OtherCompany.getValueSubmit = (
    formValues: FormValues,
    elementId: string,
    caption: string
): ClaimFormSubmitFieldValue | ClaimFormSubmitFieldValue[] | undefined => {
    const submitValue: ClaimFormSubmitFieldValue[] = [];
    const valueObj: fieldValueType = OtherCompany.getValue(formValues, elementId);
    const { companyType, remark } = valueObj;

    const companyTypeSubmitValue = getClaimFormSubmitValue('radiobuttons', elementId, caption, companyType, {
        string: companyType
    });
    submitValue.push(companyTypeSubmitValue);

    if (remark) {
        const remarkkSubmitValue = getClaimFormSubmitValue('textarea', remarkId, remarkLabel, remark, {
            string: remark
        });
        submitValue.push(remarkkSubmitValue);
    }

    const contentSubmitValue = getContentSubmitValue(companyType, formValues);

    return [...submitValue, ...contentSubmitValue];
};

OtherCompany.getValue = (formValues: FormValues, elementId: string): ElementValue => {
    const value = formValues[elementId];
    return value;
};

OtherCompany.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value = OtherCompany.getValue(formValues, elementId);

    if (value === undefined) {
        return {
            companyType: '' as CompanyType,
            remark: ''
        };
    }
    return value;
};

OtherCompany.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string): boolean => {
    const value: fieldValueType | undefined = getValue(formConfig, formValues, elementId);

    if (value && value.companyType) {
        return isContentValid(value.companyType, formValues);
    }

    return false;
};

OtherCompany.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    //Not-in-use
    return '';
};

export default OtherCompany;
